import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Radio, SwitchCamera, PhoneOff, RotateCcw, Smartphone, WifiOff, Mic, MicOff, SlidersHorizontal, Check } from "lucide-react";
import VideoTile, { trackSize } from "@/components/studio/VideoTile";
import ConnectionDot from "@/components/studio/ConnectionDot";
import { useRtkMeeting, useConnectionHealth, useUplinkStats, QUALITY } from "@/hooks/useRtkMeeting";
import { useShowSocket } from "@/hooks/useShowSocket";
import { studioApi, errMessage } from "@/lib/studioApi";

/**
 * Camera join — turn any phone / laptop into a switchable studio camera.
 * Joins with the mic OFF (the room already has audio), prefers the back camera,
 * keeps the screen awake, auto-reconnects, and shows a big tally (ON AIR) so the
 * operator knows when they're live.
 */
const LABEL_CHIPS = ["Wide shot", "Host", "Guest", "Audience", "Whiteboard", "Roaming"];
const QUALITY_ORDER = ["saver", "auto", "sharp"];
const QUALITY_KEY = "fc_cam_quality";

/** Remembered per device; "auto" (720p) until the operator picks something else. */
function useQualityPref() {
  const [quality, setQuality] = useState(() => {
    const v = typeof window !== "undefined" ? localStorage.getItem(QUALITY_KEY) : null;
    return QUALITY[v] ? v : "auto";
  });
  useEffect(() => { localStorage.setItem(QUALITY_KEY, quality); }, [quality]);
  return [quality, setQuality];
}

function QualityPicker({ value, onChange, compact = false, testId = "camera-quality" }) {
  return (
    <div className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-3"}`} data-testid={testId} role="radiogroup">
      {QUALITY_ORDER.map((k) => {
        const q = QUALITY[k];
        const on = value === k;
        return (
          <button key={k} type="button" role="radio" aria-checked={on} onClick={() => onChange(k)} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition ${on ? "border-[#c9772e] bg-[#c9772e]/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`} data-testid={`${testId}-${k}`}>
            <div><div className="text-sm font-medium">{q.label}</div><div className="text-[11px] text-zinc-400">{q.sub}</div></div>
            {on && <Check className="h-4 w-4 shrink-0 text-[#e6a25f]" />}
          </button>
        );
      })}
    </div>
  );
}

export function detectDevice() {
  const ua = navigator.userAgent || "";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "iPad";
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? "Android phone" : "Android tablet";
  return "Laptop";
}

export default function CameraJoin() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [label, setLabel] = useState("");
  const [facing, setFacing] = useState("back");
  const [joinRes, setJoinRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quality, setQuality] = useQualityPref();
  const device = useMemo(detectDevice, []);
  const isPhone = device !== "Laptop";

  useEffect(() => {
    if (!isPhone) setFacing("front");
  }, [isPhone]);

  useEffect(() => {
    studioApi.cameraInfo(token).then((r) => { setInfo(r); setLabel(r.suggested_label || "Camera"); }).catch((e) => setError(errMessage(e, "This link isn't valid")));
  }, [token]);

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setJoinRes(await studioApi.cameraJoin(token, { label: label.trim() || "Camera", device, facing })); } catch (err) { setError(errMessage(err)); } finally { setBusy(false); }
  };

  if (error) return <Frame><p className="text-sm text-zinc-300" data-testid="camera-error">{error}</p></Frame>;
  if (!info) return <Frame><Loader2 className="h-6 w-6 animate-spin text-[#e6a25f]" /></Frame>;
  if (joinRes) return <LiveCamera token={token} me={joinRes} label={label} facing={facing} show={info.show} quality={quality} setQuality={setQuality} />;

  return (
    <Frame title={info.show.title} subtitle="Add this device as a camera">
      <form onSubmit={join} className="space-y-4" data-testid="camera-form">
        <label className="block">
          <span className="text-xs font-medium text-zinc-400">Camera label (what the producer sees)</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-[#0a0f17] px-3 py-2.5 text-sm outline-none focus:border-[#c9772e]" data-testid="camera-label" />
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="camera-label-chips">
            {LABEL_CHIPS.map((c) => (
              <button key={c} type="button" onClick={() => setLabel(c)} className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${label === c ? "border-[#c9772e] bg-[#c9772e]/15 text-[#e6a25f]" : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"}`} data-testid={`camera-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>
            ))}
          </div>
        </label>
        {isPhone && (
          <div>
            <span className="text-xs font-medium text-zinc-400">Which camera</span>
            <div className="mt-1 grid grid-cols-2 gap-2" data-testid="camera-facing">
              {[["back", "Back camera", "Best quality · recommended"], ["front", "Front camera", "See yourself while filming"]].map(([v, t, s]) => (
                <button key={v} type="button" onClick={() => setFacing(v)} className={`rounded-lg border px-3 py-2.5 text-left transition ${facing === v ? "border-[#c9772e] bg-[#c9772e]/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`} data-testid={`camera-facing-${v}`}>
                  <div className="text-sm font-medium">{t}</div>
                  <div className="text-[11px] text-zinc-400">{s}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <span className="text-xs font-medium text-zinc-400">Picture quality <span className="text-zinc-600">· remembered on this device</span></span>
          <div className="mt-1"><QualityPicker value={quality} onChange={setQuality} /></div>
          <p className="mt-1.5 text-[11px] text-zinc-500">{quality === "sharp" ? "Uses up to ~4.5 Mbps upload and more battery. If the picture stutters, switch back to Auto." : quality === "saver" ? "About 1 Mbps upload — best on crowded venue Wi‑Fi or a weak cell signal." : "720p at about 2 Mbps upload — right for most rooms."}</p>
        </div>
        <button type="submit" disabled={busy} className="w-full rounded-lg bg-[#c9772e] px-4 py-3 text-sm font-semibold text-white hover:bg-[#d98a3f] disabled:opacity-50" data-testid="camera-submit">{busy ? "Connecting…" : "Start camera"}</button>
        <p className="flex items-center justify-center gap-1.5 text-center text-xs text-zinc-500"><Smartphone className="h-3.5 w-3.5" /> {device} · mic stays off · {isPhone ? "turn sideways for a wide shot" : "leave this tab open"}</p>
      </form>
    </Frame>
  );
}

function Frame({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0a0f17] px-4 py-10 text-zinc-100" data-testid="camera-join">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <img src="/logo-mark.png" alt="" className="h-11 w-11" />
          <div className="leading-tight"><div className="text-sm font-bold tracking-[0.2em]">FRIDAY</div><div className="text-[10px] font-semibold tracking-[0.35em] text-[#e6a25f]">CONVERSATIONS</div></div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0f1520] p-6 shadow-2xl">
          {title && <h1 className="text-lg font-semibold">{title}</h1>}
          {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** Remounts the RealtimeKit session (fresh client, same token) when the room connection is lost or the quality changes. */
function LiveCamera(props) {
  const [attempt, setAttempt] = useState(0);
  const [reason, setReason] = useState(null); // 'kicked' | 'removed' | 'ended' | 'left'
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  if (reason) return <CameraDone reason={reason} />;
  return <CameraSession key={`${attempt}-${props.quality}`} {...props} attempt={attempt} onRetry={retry} onDone={setReason} />;
}

function CameraDone({ reason }) {
  const msg = {
    kicked: "The producer removed this camera. You can close this tab.",
    removed: "The producer removed this camera. You can close this tab.",
    ended: "The show has ended. Thanks for filming!",
    left: "Thanks! You can close this tab.",
  }[reason] || "Camera disconnected.";
  return <Frame title="Camera disconnected"><p className="text-sm text-zinc-300" data-testid="camera-left">{msg}</p></Frame>;
}

function pickFacing(devices, facing) {
  const back = /back|rear|environment/i;
  const front = /front|user|face/i;
  const want = facing === "back" ? back : front;
  return devices.find((d) => want.test(d.label || "")) || null;
}

function CameraSession({ me, label, facing, show, attempt, onRetry, onDone, quality, setQuality }) {
  const { meeting, joined, error, leave } = useRtkMeeting(me.auth_token, { audio: false, video: true, quality, audioProfile: "studio", session: attempt });
  const [qualityOpen, setQualityOpen] = useState(false);
  const health = useConnectionHealth(meeting);
  const myHealth = meeting?.self?.id ? health[meeting.self.id] : undefined;
  const uplink = useUplinkStats(meeting, joined);
  const [removedByProducer, setRemovedByProducer] = useState(false);
  const [notice, setNotice] = useState(null);
  const meetingRef = useRef(null);
  meetingRef.current = meeting;
  const onShowEvent = useCallback((msg) => {
    if (msg.type === "participant_removed" && msg.participant_id === me.participant_id) setRemovedByProducer(true);
    if (msg.type === "mic_control" && (msg.participant_ids || []).includes(me.participant_id)) {
      const self = meetingRef.current?.self;
      if (!self) return;
      try { if (msg.enabled) self.enableAudio(); else self.disableAudio(); } catch (e) { /* noop */ }
      setNotice(msg.enabled ? `${msg.by || "The producer"} turned this phone’s mic on` : `${msg.by || "The producer"} muted this phone’s mic`);
      setTimeout(() => setNotice(null), 4000);
    }
  }, [me.participant_id]);
  const { show: liveShow } = useShowSocket(show.show_id, { participantId: me.participant_id, joinKey: me.join_key }, undefined, onShowEvent);
  const [devices, setDevices] = useState([]);
  const [net, setNet] = useState("connected"); // socket/media connection state from RTK
  const [lost, setLost] = useState(false); // room dropped -> we will remount
  const [portrait, setPortrait] = useState(false);
  const wakeRef = useRef(null);
  const seenRef = useRef(false); // we appeared in at least one snapshot
  const [, force] = useState(0);

  const reg = useMemo(() => (liveShow?.participants || []).find((p) => p.participant_id === me.participant_id), [liveShow, me.participant_id]);
  if (reg) seenRef.current = true;
  const shownLabel = reg?.label || reg?.name || label;

  const onAir = useMemo(() => {
    const p = liveShow?.program;
    if (!p) return false;
    return p.layout === "speaker" ? false : (p.sources || []).includes(me.participant_id);
  }, [liveShow, me.participant_id]);

  // Keep the phone awake while it's a camera (re-acquire after the tab is backgrounded).
  useEffect(() => {
    let released = false;
    const acquire = () => {
      if (released || document.visibilityState !== "visible") return;
      navigator.wakeLock?.request?.("screen").then((l) => { if (released) l.release(); else wakeRef.current = l; }).catch(() => {});
    };
    acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => { released = true; document.removeEventListener("visibilitychange", acquire); wakeRef.current?.release?.(); };
  }, []);

  // Portrait nudge (phones only).
  useEffect(() => {
    const check = () => setPortrait(window.innerHeight > window.innerWidth && detectDevice() !== "Laptop");
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => { window.removeEventListener("resize", check); window.removeEventListener("orientationchange", check); };
  }, []);

  // Device list + preferred facing + media/socket health + room-loss detection.
  useEffect(() => {
    if (!meeting || !joined) return undefined;
    (async () => {
      try {
        const list = (await meeting.self.getVideoDevices()) || [];
        setDevices(list);
        const cur = meeting.self.getCurrentDevices?.()?.video;
        const want = pickFacing(list, facing);
        if (want && want.deviceId !== cur?.deviceId) await meeting.self.setDevice(want);
      } catch (e) { /* noop */ }
    })();
    const h = () => force((x) => x + 1);
    const onNet = ({ state }) => setNet(state || "connected");
    const onLeft = ({ state } = {}) => {
      if (state === "kicked") onDone("kicked");
      else if (state === "ended") onDone("ended");
      else if (state === "disconnected" || state === "failed") setLost(true);
    };
    meeting.self.on("videoUpdate", h);
    meeting.self.on("audioUpdate", h);
    meeting.self.on("deviceUpdate", h);
    meeting.self.on("deviceListUpdate", h);
    meeting.self.on("roomLeft", onLeft);
    meeting.meta?.on?.("socketConnectionUpdate", onNet);
    meeting.meta?.on?.("mediaConnectionUpdate", onNet);
    return () => {
      try {
        meeting.self.off("videoUpdate", h); meeting.self.off("audioUpdate", h); meeting.self.off("deviceUpdate", h); meeting.self.off("deviceListUpdate", h); meeting.self.off("roomLeft", onLeft);
        meeting.meta?.off?.("socketConnectionUpdate", onNet); meeting.meta?.off?.("mediaConnectionUpdate", onNet);
      } catch (e) { /* noop */ }
    };
  }, [meeting, joined, facing, onDone]);

  // Lost the room (after RTK's own retries) -> remount with a fresh client, backing off.
  useEffect(() => {
    if (!lost && !error) return undefined;
    const delay = Math.min(30000, 1500 * 2 ** Math.min(attempt, 4));
    const id = setTimeout(onRetry, delay);
    return () => clearTimeout(id);
  }, [lost, error, attempt, onRetry]);

  const flip = useCallback(async () => {
    if (!meeting || devices.length < 2) return;
    const cur = meeting.self.getCurrentDevices?.()?.video?.deviceId;
    const idx = devices.findIndex((d) => d.deviceId === cur);
    const next = devices[(idx + 1) % devices.length];
    try { await meeting.self.setDevice(next); } catch (e) { /* noop */ }
  }, [meeting, devices]);

  const stop = useCallback(async (why = "left") => { await leave(); onDone(why); }, [leave, onDone]);
  const micOn = !!(joined && meeting?.self?.audioEnabled);
  const toggleMic = useCallback(async () => {
    const self = meeting?.self;
    if (!self) return;
    try { if (self.audioEnabled) await self.disableAudio(); else await self.enableAudio(); } catch (e) { setNotice("Mic permission was blocked — allow the microphone in your browser settings"); setTimeout(() => setNotice(null), 5000); }
    force((x) => x + 1);
  }, [meeting]);
  const ended = liveShow?.status === "ended";
  // Removed participants drop out of the public snapshot, so "was here, now gone" == removed.
  const removed = removedByProducer || reg?.status === "removed" || (!!liveShow && seenRef.current && !reg);
  useEffect(() => { if (ended) stop("ended"); else if (removed) stop("removed"); }, [ended, removed, stop]);

  const size = joined ? trackSize(meeting?.self) : null;
  const reconnecting = lost || !!error || ["reconnecting", "disconnected", "failed", "connecting"].includes(net);
  const sendingPortrait = size ? size.h > size.w : false;

  return (
    <div className={`fixed inset-0 bg-black text-white transition-shadow duration-300 ${onAir ? "shadow-[inset_0_0_0_10px_#dc2626]" : ""}`} data-testid="camera-live" data-on-air={onAir ? "1" : "0"}>
      <VideoTile rtk={meeting?.self && joined ? meeting.self : null} name={shownLabel} fit="contain" className="h-full w-full" />
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-4">
        <div className="space-y-1">
          <div className="rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold" data-testid="camera-live-label">{shownLabel}</div>
          {size && <div className="rounded-full bg-black/60 px-3 py-1 text-[11px] text-zinc-300" data-testid="camera-resolution">{size.w}×{size.h}{sendingPortrait ? " · portrait" : ""}</div>}
          {joined && <ConnectionDot entry={myHealth} uplink={uplink} direction="out" size="badge" testId="camera-connection" />}
        </div>
        {onAir ? (
          <div className="flex items-center gap-2 rounded-full bg-red-600 px-5 py-2 text-base font-bold uppercase tracking-widest shadow-lg" data-testid="camera-on-air"><Radio className="h-5 w-5 animate-pulse" /> On air</div>
        ) : (
          <div className="rounded-full bg-black/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-300" data-testid="camera-off-air">{joined && !reconnecting ? "Standby" : "Connecting…"}</div>
        )}
      </div>

      {portrait && joined && !onAir && (
        <div className="pointer-events-none absolute inset-x-6 top-24 flex items-center justify-center gap-2 rounded-xl bg-[#c9772e]/90 px-4 py-3 text-center text-sm font-medium" data-testid="camera-portrait-nudge">
          <RotateCcw className="h-5 w-5 shrink-0" /> Turn your phone sideways for a wide 16:9 shot
        </div>
      )}
      {notice && (
        <div className="pointer-events-none absolute inset-x-6 bottom-28 flex items-center justify-center gap-2 rounded-xl bg-black/75 px-4 py-3 text-center text-sm font-medium" data-testid="camera-mic-notice">
          {micOn ? <Mic className="h-4 w-4 shrink-0 text-emerald-300" /> : <MicOff className="h-4 w-4 shrink-0 text-red-300" />} {notice}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-4 p-5">
        {devices.length > 1 && (
          <button type="button" onClick={flip} className="rounded-full bg-white/15 p-4 backdrop-blur hover:bg-white/25" title="Switch camera" data-testid="camera-flip"><SwitchCamera className="h-6 w-6" /></button>
        )}
        <button type="button" onClick={toggleMic} disabled={!joined} className={`rounded-full p-4 backdrop-blur ${micOn ? "bg-emerald-600/90 hover:bg-emerald-500" : "bg-white/15 hover:bg-white/25"}`} title={micOn ? "Mic is on — tap to mute" : "Use this phone as a mic"} data-testid="camera-mic" data-mic={micOn ? "on" : "off"}>{micOn ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}</button>
        <button type="button" onClick={() => setQualityOpen((v) => !v)} className={`relative rounded-full p-4 backdrop-blur ${qualityOpen ? "bg-[#c9772e]/90" : "bg-white/15 hover:bg-white/25"}`} title={`Picture quality: ${QUALITY[quality].label}`} data-testid="camera-quality-button" data-quality={quality}>
          <SlidersHorizontal className="h-6 w-6" />
          <span className="absolute -right-1 -top-1 rounded-full bg-black/80 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-200">{QUALITY[quality].height}p</span>
        </button>
        <button type="button" onClick={() => stop("left")} className="rounded-full bg-red-600/90 p-4 hover:bg-red-500" title="Disconnect" data-testid="camera-stop"><PhoneOff className="h-6 w-6" /></button>
      </div>

      {qualityOpen && (
        <div className="absolute inset-x-4 bottom-28 mx-auto max-w-sm rounded-2xl border border-white/10 bg-[#0f1520]/95 p-4 shadow-2xl backdrop-blur" data-testid="camera-quality-sheet">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold">Picture quality</div>
            <div className="text-[11px] text-zinc-500">Changing reconnects the camera for a few seconds</div>
          </div>
          <QualityPicker compact value={quality} onChange={(k) => { setQualityOpen(false); if (k !== quality) setQuality(k); }} testId="camera-quality-live" />
        </div>
      )}

      {(!joined || reconnecting) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 text-sm" data-testid="camera-connecting">
          {reconnecting && joined ? <WifiOff className="h-7 w-7 text-amber-300" /> : <Loader2 className="h-6 w-6 animate-spin" />}
          <div>{lost || error ? `Connection lost — reconnecting${attempt ? ` (try ${attempt + 1})` : ""}…` : reconnecting && joined ? "Weak connection — reconnecting…" : "Connecting camera…"}</div>
          {(lost || error) && <button type="button" onClick={onRetry} className="rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold hover:bg-white/25" data-testid="camera-retry">Retry now</button>}
        </div>
      )}
    </div>
  );
}
